import { useQueryClient } from '@tanstack/react-query'
import { useContext, useState } from 'react'

import { usePatchBrokersBrokerAttentionNeededItems } from 'utils/web_api'

import NeedsAttentionContext from '../../context/NeedsAttentionContext'
import { useGetTagsForBrokerage } from '../../hooks/needs_attention/useGetTagsForBrokerage'
import ComboboxMultiSelectFilter, {
  type ComboboxFilterGroup,
  type ComboboxFilterOption,
} from '../../reusable/table/ComboboxMultiSelectFilter'
import { type Tag } from '../types'
import NAVAssignmentDropdown from './NAVAssignmentDropdown'
import {
  filterByLabelAndExactValueCallback,
  getTagFilterOptions,
  setMissingPaymentsQueryDataCallback,
} from './utils'

type AssignTagsToNAVItemProps = {
  navItemId: string
  assignedTags: Tag[]
}

const AssignTagsToNAVItem = ({
  navItemId,
  assignedTags,
}: AssignTagsToNAVItemProps) => {
  const queryClient = useQueryClient()
  const { getBrokersMissingPaymentsQueryKey } = useContext(
    NeedsAttentionContext
  )
  const [query, setQuery] = useState('')

  const { data: tags } = useGetTagsForBrokerage()

  const options: ComboboxFilterGroup<string>[] = getTagFilterOptions(tags)

  const tagOptions: ComboboxFilterOption<string>[] =
    tags?.map((tag) => ({
      label: tag.name,
      value: tag.id,
    })) || []

  const addTagsToNAVItemMutation = usePatchBrokersBrokerAttentionNeededItems()

  const assignedTagIds = assignedTags.map((tag) => tag.id)

  const handleSelectTag = (tagId: string) => {
    addTagsToNAVItemMutation.mutate(
      {
        data: {
          broker_attention_needed_item_ids: [navItemId],
          ...(assignedTagIds.includes(tagId)
            ? { tag_ids_to_remove: [tagId] }
            : { tag_ids_to_add: [tagId] }),
        },
      },
      {
        onSuccess: (response) => {
          // Update cached data with new NAV items that include updated tags.
          // TODO: Investigate looking at query params and removing item if not part of the current page.
          queryClient.setQueryData(
            getBrokersMissingPaymentsQueryKey,
            setMissingPaymentsQueryDataCallback(response)
          )
        },
      }
    )
  }

  const filteredOptions = options?.map((group) => ({
    ...group,
    options: group.options.filter(filterByLabelAndExactValueCallback(query)),
  }))

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <ComboboxMultiSelectFilter<string>
        selectedValues={assignedTagIds}
        options={filteredOptions}
        onSelectValue={handleSelectTag}
        placeholder='Search tags to assign...'
        onValueChange={setQuery}
      >
        <NAVAssignmentDropdown
          noSelectionText='Tag'
          pluralIdentifier='tags'
          selectedValues={assignedTagIds}
          options={tagOptions || []}
          isPending={addTagsToNAVItemMutation.isPending}
          type='tag'
        />
      </ComboboxMultiSelectFilter>
    </div>
  )
}

export default AssignTagsToNAVItem
